import jsPDF from 'jspdf';

const BRAND = 'THE ELEVENTH';
const BRAND_SUB = 'Interior Design Studio';
const PRIMARY_COLOR = [59, 105, 120]; // #3b6978
const ACCENT_COLOR = [201, 169, 110]; // #c9a96e
const TEXT_COLOR = [30, 30, 30];
const MUTED_COLOR = [120, 120, 120];

function addHeader(doc, title) {
  // Brand name
  doc.setFontSize(18);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text(BRAND, 20, 20);
  doc.setFontSize(8);
  doc.setTextColor(...MUTED_COLOR);
  doc.text(BRAND_SUB, 20, 26);

  // Title on right
  doc.setFontSize(14);
  doc.setTextColor(...TEXT_COLOR);
  doc.text(title, doc.internal.pageSize.width - 20, 20, { align: 'right' });

  // Accent line
  doc.setDrawColor(...ACCENT_COLOR);
  doc.setLineWidth(0.5);
  doc.line(20, 32, doc.internal.pageSize.width - 20, 32);

  return 38;
}

function addFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...MUTED_COLOR);
    doc.text(`${BRAND} — Confidential`, 20, doc.internal.pageSize.height - 10);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, { align: 'right' });
  }
}

const fmt = (n) => typeof n === 'number' ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';

export function generateProposalPDF(proposal) {
  const doc = new jsPDF();
  let y = addHeader(doc, 'PROPOSAL');

  // Meta
  doc.setFontSize(11);
  doc.setTextColor(...TEXT_COLOR);
  doc.text(proposal.title || 'Design Proposal', 20, y);
  y += 7;
  doc.setFontSize(9);
  doc.setTextColor(...MUTED_COLOR);
  if (proposal.client_name) { doc.text(`Client: ${proposal.client_name}`, 20, y); y += 5; }
  if (proposal.project_name) { doc.text(`Project: ${proposal.project_name}`, 20, y); y += 5; }
  doc.text(`Status: ${proposal.status?.toUpperCase()}`, 20, y); y += 5;
  if (proposal.valid_until) { doc.text(`Valid Until: ${new Date(proposal.valid_until).toLocaleDateString()}`, 20, y); y += 5; }
  y += 5;

  // Table header
  const cols = [20, 90, 110, 135, 165, 190];
  doc.setFillColor(245, 243, 240);
  doc.rect(20, y, 170, 7, 'F');
  doc.setFontSize(7);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('Description', cols[0] + 2, y + 5);
  doc.text('Qty', cols[1] + 2, y + 5);
  doc.text('Trade', cols[2] + 2, y + 5);
  doc.text('Markup', cols[3] + 2, y + 5);
  doc.text('Client', cols[4] + 2, y + 5);
  y += 10;

  // Items
  doc.setTextColor(...TEXT_COLOR);
  doc.setFontSize(8);
  for (const item of (proposal.items || [])) {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.text(String(item.description || '').substring(0, 40), cols[0] + 2, y);
    doc.text(String(item.quantity || 1), cols[1] + 2, y);
    doc.text(fmt(item.trade_price || item.total_trade / (item.quantity || 1)), cols[2] + 2, y);
    doc.text(`${item.markup_percentage || 0}%`, cols[3] + 2, y);
    doc.text(fmt(item.total_client), cols[4] + 2, y);
    y += 6;
  }

  // Totals
  y += 5;
  doc.setDrawColor(...ACCENT_COLOR);
  doc.line(120, y, 190, y);
  y += 6;
  doc.setFontSize(8);
  doc.text(`Trade Total: ${fmt(proposal.subtotal_trade)}`, 120, y); y += 5;
  doc.text(`Client Subtotal: ${fmt(proposal.subtotal_client)}`, 120, y); y += 5;
  if (proposal.tax_amount > 0) { doc.text(`Tax (${proposal.tax_rate}%): ${fmt(proposal.tax_amount)}`, 120, y); y += 5; }
  doc.setFontSize(11);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text(`Grand Total: ${fmt(proposal.grand_total)}`, 120, y);
  y += 10;

  // Notes/Terms
  if (proposal.notes) { doc.setFontSize(8); doc.setTextColor(...MUTED_COLOR); doc.text(`Notes: ${proposal.notes}`, 20, y); y += 5; }
  if (proposal.terms) { doc.setFontSize(8); doc.setTextColor(...MUTED_COLOR); doc.text(`Terms: ${proposal.terms}`, 20, y); y += 5; }

  // Signature area
  if (proposal.signature) {
    y += 10;
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_COLOR);
    doc.text('Client Signature:', 20, y);
    y += 3;
    doc.addImage(proposal.signature, 'PNG', 20, y, 60, 25);
    y += 30;
    doc.setFontSize(7);
    doc.setTextColor(...MUTED_COLOR);
    if (proposal.signed_at) doc.text(`Signed: ${new Date(proposal.signed_at).toLocaleString()}`, 20, y);
  }

  addFooter(doc);
  doc.save(`Proposal_${proposal.title?.replace(/\s+/g, '_') || 'proposal'}.pdf`);
}

export function generateInvoicePDF(invoice) {
  const doc = new jsPDF();
  let y = addHeader(doc, 'INVOICE');

  doc.setFontSize(11);
  doc.setTextColor(...TEXT_COLOR);
  doc.text(invoice.invoice_number || 'Invoice', 20, y);
  y += 7;
  doc.setFontSize(9);
  doc.setTextColor(...MUTED_COLOR);
  if (invoice.client_name) { doc.text(`Client: ${invoice.client_name}`, 20, y); y += 5; }
  doc.text(`Status: ${invoice.status?.toUpperCase()}`, 20, y); y += 5;
  doc.text(`Date: ${invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : ''}`, 20, y); y += 5;
  if (invoice.due_date) { doc.text(`Due: ${new Date(invoice.due_date).toLocaleDateString()}`, 20, y); y += 5; }
  y += 5;

  // Table
  doc.setFillColor(245, 243, 240);
  doc.rect(20, y, 170, 7, 'F');
  doc.setFontSize(7);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('Description', 22, y + 5);
  doc.text('Qty', 120, y + 5);
  doc.text('Rate', 140, y + 5);
  doc.text('Amount', 170, y + 5);
  y += 10;

  doc.setTextColor(...TEXT_COLOR);
  doc.setFontSize(8);
  for (const item of (invoice.items || [])) {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.text(String(item.description || '').substring(0, 55), 22, y);
    doc.text(String(item.quantity || 1), 120, y);
    doc.text(fmt(item.amount), 140, y);
    doc.text(fmt((item.amount || 0) * (item.quantity || 1)), 170, y);
    y += 6;
  }

  y += 5;
  doc.setDrawColor(...ACCENT_COLOR);
  doc.line(120, y, 190, y);
  y += 6;
  doc.setFontSize(8);
  doc.text(`Subtotal: ${fmt(invoice.subtotal)}`, 120, y); y += 5;
  if (invoice.tax > 0) { doc.text(`Tax: ${fmt(invoice.tax)}`, 120, y); y += 5; }
  doc.setFontSize(11);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text(`Total: ${fmt(invoice.total)}`, 120, y);

  addFooter(doc);
  doc.save(`Invoice_${invoice.invoice_number || 'invoice'}.pdf`);
}

export function generateFFESchedulePDF(items, projectName) {
  const doc = new jsPDF('l'); // landscape for wide tables
  let y = addHeader(doc, 'FF&E SCHEDULE');

  doc.setFontSize(10);
  doc.setTextColor(...TEXT_COLOR);
  doc.text(projectName || 'FF&E Schedule', 20, y);
  y += 7;
  doc.setFontSize(8);
  doc.setTextColor(...MUTED_COLOR);
  doc.text(`${items.length} items | Generated ${new Date().toLocaleDateString()}`, 20, y);
  y += 8;

  // Table header
  const hCols = [20, 75, 105, 130, 155, 175, 200, 225, 252];
  doc.setFillColor(245, 243, 240);
  doc.rect(20, y, 257, 7, 'F');
  doc.setFontSize(6.5);
  doc.setTextColor(...PRIMARY_COLOR);
  ['Item', 'Category', 'SKU', 'Material', 'Finish', 'Qty', 'Trade', 'Client', 'Status'].forEach((h, i) => doc.text(h, hCols[i] + 2, y + 5));
  y += 10;

  doc.setTextColor(...TEXT_COLOR);
  doc.setFontSize(7);
  let totalTrade = 0, totalClient = 0;
  for (const item of items) {
    if (y > 180) { doc.addPage(); y = 20; }
    doc.text(String(item.name || '').substring(0, 30), hCols[0] + 2, y);
    doc.text(String(item.category || '').substring(0, 15), hCols[1] + 2, y);
    doc.text(String(item.sku || '').substring(0, 12), hCols[2] + 2, y);
    doc.text(String(item.material || '').substring(0, 12), hCols[3] + 2, y);
    doc.text(String(item.finish || '').substring(0, 10), hCols[4] + 2, y);
    doc.text(String(item.quantity || 1), hCols[5] + 2, y);
    doc.text(fmt(item.trade_price), hCols[6] + 2, y);
    doc.text(fmt(item.client_price), hCols[7] + 2, y);
    doc.text(String(item.status || '').substring(0, 10), hCols[8] + 2, y);
    totalTrade += (item.trade_price || 0) * (item.quantity || 1);
    totalClient += (item.client_price || 0) * (item.quantity || 1);
    y += 5;
  }

  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text(`Trade Total: ${fmt(totalTrade)}  |  Client Total: ${fmt(totalClient)}  |  Markup: ${fmt(totalClient - totalTrade)}`, 20, y);

  addFooter(doc);
  doc.save(`FFE_Schedule_${projectName?.replace(/\s+/g, '_') || 'schedule'}.pdf`);
}


export function generatePurchaseOrderPDF(po, vendor, project) {
  const doc = new jsPDF();
  let y = addHeader(doc, `PO ${po.po_number}`);

  doc.setFontSize(10);
  doc.setTextColor(...TEXT_COLOR);
  doc.text(`Vendor: ${vendor?.name || '—'}`, 20, y); y += 6;
  if (vendor?.email) { doc.setTextColor(...MUTED_COLOR); doc.text(vendor.email, 20, y); y += 5; }
  if (vendor?.phone) { doc.setTextColor(...MUTED_COLOR); doc.text(vendor.phone, 20, y); y += 5; }
  doc.setTextColor(...TEXT_COLOR);
  if (project) { doc.text(`Project: ${project.name}`, 20, y); y += 6; }
  if (po.expected_delivery) { doc.text(`Expected delivery: ${String(po.expected_delivery).slice(0,10)}`, 20, y); y += 6; }
  if (po.shipping_address) { doc.text(`Ship to: ${po.shipping_address}`, 20, y); y += 6; }
  y += 4;

  // Table header
  doc.setFillColor(...PRIMARY_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(20, y - 4, 170, 7, 'F');
  doc.setFontSize(9);
  doc.text('Item', 22, y); doc.text('Qty', 120, y, { align: 'right' });
  doc.text('Unit', 150, y, { align: 'right' }); doc.text('Total', 188, y, { align: 'right' });
  y += 6;

  doc.setTextColor(...TEXT_COLOR);
  doc.setFontSize(9);
  let total = 0;
  (po.items || []).forEach(it => {
    if (y > 260) { doc.addPage(); y = 20; }
    const qty = Number(it.quantity) || 1;
    const unit = Number(it.unit_price) || 0;
    const sub = qty * unit;
    total += sub;
    doc.text(String(it.name || '').slice(0, 60), 22, y);
    doc.text(String(qty), 120, y, { align: 'right' });
    doc.text(fmt(unit), 150, y, { align: 'right' });
    doc.text(fmt(sub), 188, y, { align: 'right' });
    y += 5;
    if (it.description) {
      doc.setTextColor(...MUTED_COLOR);
      doc.setFontSize(8);
      doc.text(String(it.description).slice(0, 90), 22, y);
      doc.setFontSize(9);
      doc.setTextColor(...TEXT_COLOR);
      y += 5;
    }
  });

  y += 4;
  doc.setDrawColor(...ACCENT_COLOR);
  doc.line(130, y, 190, y);
  y += 5;
  doc.setFontSize(11);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text(`Total: ${fmt(total)}`, 188, y, { align: 'right' });

  if (po.notes) {
    y += 12;
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_COLOR);
    doc.text('Notes:', 20, y); y += 5;
    doc.setTextColor(...MUTED_COLOR);
    const lines = doc.splitTextToSize(po.notes, 170);
    doc.text(lines, 20, y);
  }

  addFooter(doc);
  doc.save(`PO_${po.po_number}.pdf`);
}
